// Updated: Added park confirmation flow, participant details display, and awaiting_park_confirmation status

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, CreditCard, Banknote, ShieldAlert, FileText } from 'lucide-react';
import { Tour } from '../../types/tours';
import { BookingParticipant } from '../../types/bookings';
import { formatDate, formatTime } from '../../utils/dateUtils';

interface BookingConfirmStepProps {
  tour: Tour;
  data: {
    date: Date | null;
    groupType: 'group' | 'reduced' | 'private' | null;
    persons: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    totalPrice: number;
    termsAccepted: boolean;
    paymentMethod?: 'cash' | 'paypal';
    bootSizes?: string[];
    participants?: BookingParticipant[];
  };
  onChange: (data: { termsAccepted?: boolean; paymentMethod?: 'cash' | 'paypal'; bookingCode?: string }) => void;
  onBack: () => void;
  onNext: () => void;
}

const BookingConfirmStep: React.FC<BookingConfirmStepProps> = ({
  tour,
  data,
  onChange,
  onBack,
  onNext
}) => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const paymentMethod = data.paymentMethod || 'cash';

  // Calculate PayPal commission based on price ranges
  const calculatePayPalCommission = (amount: number): number => {
    if (amount >= 1 && amount <= 50.99) return 4;
    if (amount >= 51 && amount <= 100.99) return 6;
    if (amount >= 101 && amount <= 150.99) return 7;
    if (amount >= 151 && amount <= 200.99) return 8;
    if (amount >= 201 && amount <= 250.99) return 9;
    if (amount >= 251 && amount <= 300.99) return 10;
    if (amount >= 301 && amount <= 350.99) return 11;
    if (amount >= 351 && amount <= 400.99) return 12;
    if (amount >= 401 && amount <= 500.99) return 14;
    if (amount >= 501 && amount <= 1000.99) return 24;
    if (amount >= 1001 && amount <= 1500.99) return 34;
    return 34; // For amounts above $1500.99
  };

  const paypalCommission = calculatePayPalCommission(data.totalPrice);
  const finalTotal = paymentMethod === 'paypal' ? data.totalPrice + paypalCommission : data.totalPrice;

  const handleSubmit = async () => {
    // Prevent double submissions
    if (submitting) {
      return;
    }

    if (!data.termsAccepted) {
      setError('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { supabase } = await import('../../lib/supabase');

      const bookingCode = `SF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

      // Save booking to database
      const bookingStatus = tour.requires_park_confirmation ? 'awaiting_park_confirmation' : 'not_confirmed';
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          tour_id: tour.id,
          booking_date: data.date,
          start_time: tour.departure,
          group_type: data.groupType,
          persons: data.persons,
          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          pickup_address: 'Las Vegas Restaurant in Sierpe',
          total_price: finalTotal,
          payment_method: paymentMethod,
          booking_code: bookingCode,
          status: bookingStatus,
          boot_sizes: data.bootSizes || []
        })
        .select()
        .single();

      if (bookingError) {
        console.error('Failed to save booking:', bookingError);
        setError('Failed to save your booking. Please try again.');
        setSubmitting(false);
        return;
      }

      // Save participant details if applicable
      if (tour.requires_participant_details && data.participants && data.participants.length > 0) {
        const participantRows = data.participants.map((p, idx) => ({
          booking_id: bookingData.id,
          full_name: p.full_name.trim(),
          passport_number: p.passport_number.trim(),
          display_order: idx
        }));

        const { error: participantsError } = await supabase
          .from('booking_participants')
          .insert(participantRows);

        if (participantsError) {
          console.error('Failed to save participants:', participantsError);
        }
      }

      // Send Telegram notification
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
        const apiUrl = `${supabaseUrl}/functions/v1/send-telegram-notification`;

        let bootSizesText = '';
        if (tour.has_rental_boots && data.bootSizes && data.bootSizes.length > 0) {
          bootSizesText = data.bootSizes.map((size, index) =>
            `P${index + 1}: Size ${size}`
          ).join(', ');
        }

        let participantsText = '';
        if (tour.requires_participant_details && data.participants && data.participants.length > 0) {
          participantsText = data.participants.map((p, index) =>
            `P${index + 1}: ${p.full_name} (Passport: ${p.passport_number})`
          ).join('\n');
        }

        await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tourName: tour.name,
            bookingDate: data.date?.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) || '',
            groupType: data.groupType || '',
            persons: data.persons,
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim(),
            phone: data.phone.trim(),
            totalPrice: finalTotal,
            paymentMethod: paymentMethod,
            bookingCode: bookingCode,
            bootSizes: bootSizesText,
            participants: participantsText,
            requiresParkConfirmation: tour.requires_park_confirmation || false
          }),
        });
      } catch (notificationError) {
        console.error('Failed to send Telegram notification:', notificationError);
      }

      // Store the booking code
      onChange({ bookingCode });

      onNext();
    } catch (err) {
      console.error('Error processing booking:', err);
      setError('An error occurred while processing your booking. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">Confirm Your Request</h2>
        <p className="text-[#2A382A]/70">
          Please review your booking request details and confirm your acceptance of our terms.
        </p>
      </div>

      {/* Booking Summary */}
      <div className="bg-[#F2F3F4] rounded-xl p-6 space-y-6">
        <h3 className="font-semibold text-[#2A382A]">Booking Summary</h3>

        <div className="space-y-4">
          <div className="flex justify-between items-center pb-4 border-b border-[#2A382A]/10">
            <div>
              <h4 className="font-medium text-[#2A382A]">Date & Time</h4>
              <p className="text-sm text-[#2A382A]/70">
                {data.date && formatDate(data.date, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-sm text-[#2A382A]/70">
                Starting at {formatTime(tour.departure).americanTime} ({formatTime(tour.departure).europeanTime})
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center pb-4 border-b border-[#2A382A]/10">
            <div className="w-full">
              <h4 className="font-medium text-[#2A382A] mb-4">Group Details</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Group Information */}
                <div>
                  <p className="text-sm text-[#2A382A]">
                    {data.groupType === 'private' 
                      ? `Private tour with ${data.persons} ${data.persons === 1 ? 'person' : 'persons'}`
                      : `${data.groupType === 'reduced' ? 'Reduced group' : 'Group'} tour for ${data.persons} ${data.persons === 1 ? 'person' : 'persons'}`}
                  </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#2A382A] font-medium">Las Vegas Restaurant in Sierpe</p>
                    <p className="text-sm text-[#2A382A]/70">VG5H+J7H, Provincia de Puntarenas, Sierpe, Costa Rica</p>
                    <p className="text-sm text-[#953B0E] font-medium mt-2">Please arrive 15 minutes before the starting time</p>
                    <a 
                      href={tour.meeting_point_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#953B0E] hover:underline mt-1 inline-block"
                    >
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>
                  <p className="text-sm text-[#2A382A] font-medium">{tour.meeting_point}</p>
          </div>

          <div className="flex justify-between items-center pb-4 border-b border-[#2A382A]/10">
            <div>
              <h4 className="font-medium text-[#2A382A]">Contact Information</h4>
              <p className="text-sm text-[#2A382A]/70">
                {data.firstName} {data.lastName}
              </p>
              <p className="text-sm text-[#2A382A]/70">{data.email}</p>
              <p className="text-sm text-[#2A382A]/70">{data.phone}</p>
            </div>
          </div>

          {/* Boot Sizes (if applicable) */}
          {tour.has_rental_boots && data.bootSizes && data.bootSizes.length > 0 && (
            <div className="flex justify-between items-center pb-4 border-b border-[#2A382A]/10">
              <div>
                <h4 className="font-medium text-[#2A382A]">Rubber Boot Sizes</h4>
                <div className="text-sm text-[#2A382A]/70 space-y-1">
                  {data.bootSizes.map((size, index) => (
                    <p key={index}>
                      Participant {index + 1}: Size {size} (US {
                        size === '35' ? '5' :
                        size === '36' ? '6' :
                        size === '37' ? '6.5' :
                        size === '38' ? '7' :
                        size === '39' ? '7.5' :
                        size === '40' ? '8' :
                        size === '41' ? '8.5' :
                        size === '42' ? '9' :
                        size === '43' ? '9.5' :
                        size === '44' ? '10' :
                        size === '45' ? '11' :
                        size === '46' ? '12' :
                        size === '47' ? '13' :
                        size === '48' ? '14' : size
                      })
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Participant Details (for park confirmation tours) */}
          {tour.requires_participant_details && data.participants && data.participants.length > 0 && (
            <div className="pb-4 border-b border-[#2A382A]/10">
              <h4 className="font-medium text-[#2A382A] mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Participant Details
              </h4>
              <div className="text-sm text-[#2A382A]/70 space-y-1">
                {data.participants.map((p, index) => (
                  <p key={index}>
                    {index + 1}. {p.full_name} - Passport: {p.passport_number}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Park Confirmation Notice */}
      {tour.requires_park_confirmation && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">Awaiting Park Confirmation</p>
            <p>This booking is subject to availability at Corcovado National Park. After submitting, we will request your spot with the park and confirm within 24 hours via email.</p>
          </div>
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="bg-[#F2F3F4] rounded-xl p-6">
        <h4 className="font-semibold text-[#2A382A] mb-4">Payment Method</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onChange({ paymentMethod: 'cash' })}
            className={`p-4 rounded-lg border-2 transition-all ${
              paymentMethod === 'cash'
                ? 'border-[#953B0E] bg-[#953B0E]/5'
                : 'border-[#2A382A]/10 hover:border-[#953B0E]/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <Banknote className="w-6 h-6 text-[#2A382A]" />
              <div className="text-left">
                <h5 className="font-medium text-[#2A382A]">Cash Payment</h5>
                <p className="text-sm text-[#2A382A]/70">Pay upon arrival</p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onChange({ paymentMethod: 'paypal' })}
            className={`p-4 rounded-lg border-2 transition-all ${
              paymentMethod === 'paypal'
                ? 'border-[#953B0E] bg-[#953B0E]/5'
                : 'border-[#2A382A]/10 hover:border-[#953B0E]/50'
            }`}
          >
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-[#2A382A]" />
              <div className="text-left">
                <h5 className="font-medium text-[#2A382A]">PayPal Transfer</h5>
                <p className="text-sm text-[#2A382A]/70">Pay upon arrival via PayPal</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="bg-[#F2F3F4] rounded-xl p-6">
        <h4 className="font-semibold text-[#2A382A] mb-4">Price Breakdown</h4>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-[#2A382A]/70">Tour Price</span>
            <span className="font-medium text-[#2A382A]">${data.totalPrice.toFixed(2)}</span>
          </div>
          
          {paymentMethod === 'paypal' && (
            <div className="flex justify-between items-center">
              <span className="text-[#2A382A]/70">PayPal Commission</span>
              <span className="font-medium text-[#2A382A]">${paypalCommission.toFixed(2)}</span>
            </div>
          )}
          
          <div className="border-t border-[#2A382A]/10 pt-3">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-[#2A382A]">Total Amount</span>
              <span className="font-bold text-xl text-[#2A382A]">${finalTotal.toFixed(2)}</span>
            </div>
            {paymentMethod === 'paypal' && (
              <div className="mt-2 text-sm text-[#953B0E]">
                PayPal commission of ${paypalCommission.toFixed(2)} included to cover transaction fees.
              </div>
            )}
          </div>
        </div>
        
      </div>

      {/* Payment Information */}
      <div className="bg-[#F2F3F4] rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#953B0E] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-[#2A382A] mb-2">Payment Information</h4>
            <p className="text-sm text-[#2A382A]/70">
              Payment will be collected upon arrival at the meeting point. Online payment is not available at this time.
            </p>
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={data.termsAccepted}
              onChange={(e) => onChange({ termsAccepted: e.target.checked })}
            />
            <div className="w-5 h-5 border-2 border-[#2A382A]/20 rounded group-hover:border-[#953B0E] transition-colors peer-checked:border-[#953B0E] peer-checked:bg-[#953B0E]" />
            <Check className="w-3 h-3 text-white absolute left-1 top-1 opacity-0 peer-checked:opacity-100 transition-opacity" />
          </div>
          <span className="text-sm text-[#2A382A]/70">
            I agree to the{' '}
            <Link 
              to="/terms-and-conditions" 
              aria-label="Read our Terms and Conditions"
              className="text-[#953B0E] hover:underline"
            >Terms & Conditions</Link>
            {' '}and{' '}
            <Link 
              to="/privacy-policy" 
              aria-label="Read our Privacy Policy"
              className="text-[#953B0E] hover:underline"
            >Privacy Policy</Link>
          </span>
        </label>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={submitting}
          className="px-6 py-2 bg-white border border-[#2A382A]/10 text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : (
            tour.requires_park_confirmation ? 'Submit Reservation Request' : 'Submit Request'
          )}
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmStep;